import { makeObservable, observable, flow, action, autorun } from "mobx";
import _ from "lodash";
import * as richmodalService from "../../services/richmodal";
import {
  delay,
  extractUrls,
  getHtmlInfoByUrl,
  splitStrByUrl,
} from "../../utils";
import {
  ESessionFileContentParseStatus,
  ILinkFileContent,
  ISessionFile,
  ESessionFileType,
  ESessionFileStatus,
} from "./type";
import {
  ERichModalFileParseStatus,
  IRichModalFileCommitInfo,
  IRichModalFileStatusInfo,
} from "../file/types";
import { IChatTextMessage } from "../chat/types";

// 根据状态获取文件错误提示
export const getLinkFilePreviewTip = (
  fileStatus: ESessionFileStatus | ERichModalFileParseStatus,
  contentParseStatus: ESessionFileContentParseStatus,
  parseContentRes: any,
): {
  status: "" | "success" | "error";
  tip: string;
} => {
  if (fileStatus !== "success") {
    if (fileStatus === ERichModalFileParseStatus.running) {
      return {
        status: "",
        tip: "解析网址中，请稍后…",
      };
    }
    if (fileStatus === ESessionFileStatus.deleted) {
      return { status: "error", tip: "已删除，商量回答时不会继续参考" };
    }
    return { status: "error", tip: "无效网址，商量无法阅读" };
  }

  // 文件上传成功
  if (
    [
      ESessionFileContentParseStatus.pending,
      ESessionFileContentParseStatus.parsing,
    ].includes(contentParseStatus)
  ) {
    return {
      status: "",
      tip: "解析网址中，请稍后…",
    };
  }

  if (contentParseStatus === ESessionFileContentParseStatus.failed) {
    return { status: "error", tip: "由于该网站限制，商量无法阅读" };
  }

  const hasContentRes = !_.isEmpty(parseContentRes);
  if (
    contentParseStatus === ESessionFileContentParseStatus.success &&
    !hasContentRes
  ) {
    return { status: "success", tip: "该网址暂不支持预览" };
  }

  return { status: "success", tip: "" };
};

class SessionFile implements ISessionFile {
  @observable type: ESessionFileType;
  @observable status: ESessionFileStatus | ERichModalFileParseStatus;
  @observable contentParseStatus: ESessionFileContentParseStatus; // 文件内容解析状态
  @observable contentParseRes?: ILinkFileContent;
  @observable file_id?: string;

  content_type?: string;
  file_name: string;
  url: string;
  size?: number;
  token?: number;
  total_page?: number;

  isPolling: boolean = false;

  constructor({
    type,
    url,
    file_id,
    file_name,
    contentParseStatus,
    status,
  }: ISessionFile) {
    this.type = type;
    this.file_id = file_id;
    this.file_name = file_name;
    this.url = url;
    this.status = status;
    this.contentParseStatus = contentParseStatus;
    this.isPolling = false;
    makeObservable(this);
    if (
      type === ESessionFileType.link &&
      status === ERichModalFileParseStatus.running
    ) {
      this.commitLinkFile();
    }

    autorun(() => {
      if (
        !!this.url &&
        this.type === ESessionFileType.link &&
        [
          ESessionFileStatus.success,
          ERichModalFileParseStatus.success,
        ].includes(this.status) &&
        this.contentParseStatus === ESessionFileContentParseStatus.parsing
      ) {
        this.getPreviewInfoByUrl();
      }
    });
  }

  setUrl(v: string) {
    this.url = v;
  }

  setFileId(v: string) {
    this.file_id = v;
  }

  setFileName(v: string) {
    this.file_name = v;
  }

  setStatus(v: ESessionFileStatus | ERichModalFileParseStatus) {
    this.status = v;
  }

  setContentParseStatus(v: ESessionFileContentParseStatus) {
    this.contentParseStatus = v;
  }

  @action
  updateWhenParseError(status: ESessionFileStatus | ERichModalFileParseStatus) {
    this.setStatus(status);
    this.setContentParseStatus(ESessionFileContentParseStatus.failed);
    this.isPolling && (this.isPolling = false);
  }

  @flow
  *commitLinkFile() {
    try {
      this.setStatus(ERichModalFileParseStatus.running);
      const [res]: IRichModalFileCommitInfo[] =
        yield richmodalService.commitFile([], [this.file_name]);
      if (res?.status === ERichModalFileParseStatus.success) {
        this.setFileId(res.file_id);
        this.setFileName(res.link!);
        // this.setStatus(ERichModalFileParseStatus.running);
        this.setContentParseStatus(ESessionFileContentParseStatus.pending);
        this.startPollingStatus();
      } else {
        this.updateWhenParseError(
          ERichModalFileParseStatus[res.status] ||
            ERichModalFileParseStatus.failed,
        );
      }
    } catch (error) {
      this.updateWhenParseError(ERichModalFileParseStatus.failed);
    }
  }

  async startPollingStatus() {
    if (this.isPolling || !this.file_id) return;
    try {
      this.isPolling = true;
      while (this.isPolling) {
        const isContinue = await this.queryFileParseStatus();
        if (!isContinue) {
          this.isPolling = false;
        } else {
          await delay(5000);
        }
      }
    } catch (error) {
      this.updateWhenParseError(ERichModalFileParseStatus.failed);
    }
  }

  async queryFileParseStatus() {
    const [res]: IRichModalFileStatusInfo[] =
      await richmodalService.getParseStatus([this.file_id!]);

    if (res?.status !== ERichModalFileParseStatus.running) {
      this.setStatus(res?.status);
      if (res?.status === ERichModalFileParseStatus.success) {
        this.getLinkFileUrl();
      } else {
        this.setContentParseStatus(ESessionFileContentParseStatus.failed);
      }
      return false;
    }
    return true;
  }

  // 获取link对应原文url
  async getLinkFileUrl() {
    try {
      if (this.file_id && this.status === "success") {
        const { url } = await richmodalService.getFileDetail(this.file_id);
        this.setUrl(url);
        this.setContentParseStatus(ESessionFileContentParseStatus.parsing);
        this.getPreviewInfoByUrl();
      }
    } catch (error) {
      this.setContentParseStatus(ESessionFileContentParseStatus.failed);
    }
  }

  // 解析url 内容
  async getPreviewInfoByUrl() {
    this.setContentParseStatus(ESessionFileContentParseStatus.parsing);
    try {
      const parseInfo = await getHtmlInfoByUrl(this.url);

      if (parseInfo) {
        this.contentParseRes = {
          sourceIcon: parseInfo.favicon,
          title: parseInfo.title,
          desc: parseInfo.description,
        };
      }
      this.setContentParseStatus(ESessionFileContentParseStatus.success);
    } catch (error) {
      this.setContentParseStatus(ESessionFileContentParseStatus.failed);
    }
  }

  static getLinksFromText(text: string) {
    if (!text) return [];
    return extractUrls(text);
  }

  static splitUrlAndStr(text: string): IChatTextMessage[] {
    return splitStrByUrl(text);
  }

  // 提取文本中url，并生成LinkFileModel
  static instanceFileByText(text: string) {
    const urls = this.getLinksFromText(text);
    if (!urls.length) return [];
    return urls.map(
      url =>
        new SessionFile({
          type: ESessionFileType.link,
          file_name: url,
          url: "",
          status: ERichModalFileParseStatus.running,
          contentParseStatus: ESessionFileContentParseStatus.pending,
        }),
    );
  }
}

export default SessionFile;
